import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ProductInterestCTA from '../../components/feature/ProductInterestCTA';
import { featuredProducts } from '../../mocks/products';

// Helper function to get series key for translations
const getSeriesKey = (series: string): string => {
  const seriesMap: Record<string, string> = {
    'Royal Diamond': 'royalDiamond',
    'Decori': 'decori',
    'Modern Steel': 'modernSteel',
    'Fortuna': 'fortuna',
    'Fortuna Supreme': 'fortunaSupreme',
    'Mazar': 'mazar',
    'Mazar 3D': 'mazar3D',
    'Deep Diamond': 'deepDiamond',
    'Verdi Fires': 'verdiFires'
  };
  return seriesMap[series] || 'royalDiamond';
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation(['common', 'products']);

  // Categories with i18n
  const categories = [
    {
      slug: 'vrijstaande-haarden',
      key: 'freestandingFireplaces',
      image: 'https://sfeerhaardkopen.com/cdn/shop/files/VerdiFires0.jpg?v=1766689629',
      modelCount: 1
    },
    {
      slug: 'inbouwhaarden',
      key: 'builtIn',
      image: 'https://diamondflame.nl/cdn/shop/files/RoyalDiamond1_774bf3ad-1297-4eaf-b41e-f1f18df8e26a.png',
      modelCount: 13
    },
    {
      slug: '3-zijdige-haarden',
      key: 'threeSided',
      image: 'https://diamondflame.nl/cdn/shop/files/DeepDiamondhaard.jpg?v=1759795057',
      modelCount: 9
    }
  ];

  // Find category
  const category = categories.find(cat => cat.slug === slug);

  // Filter products based on category
  const categoryProducts = featuredProducts.filter(product => {
    if (slug === 'vrijstaande-haarden') return product.category === 'Vrijstaande Haarden';
    if (slug === 'inbouwhaarden') return product.category === 'Inbouwhaarden';
    if (slug === '3-zijdige-haarden') return product.category === '3-zijdige Haarden';
    return false;
  });

  if (!category) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('products:common.productNotFound')}</h1>
          <Link to="/" className="text-amber-600 hover:text-amber-700 underline cursor-pointer">
            {t('products:common.backToHome')}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative py-24 bg-gray-900">
        <div className="absolute inset-0">
          <img
            src={category.image}
            alt={t(`common:categories.${category.key}`)}
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="mb-8">
            <ol className="flex items-center gap-2 text-sm">
              <li>
                <Link to="/" className="text-gray-300 hover:text-white transition-colors cursor-pointer">
                  {t('common:nav.home')}
                </Link>
              </li>
              <li className="text-gray-500">/</li>
              <li className="text-white font-medium">{t(`common:categories.${category.key}`)}</li>
            </ol>
          </nav>

          <div className="max-w-3xl">
            <div className="inline-block px-4 py-2 bg-amber-500/90 rounded-full mb-6">
              <span className="text-xs font-semibold uppercase tracking-wider text-black">
                {categoryProducts.length} {t('common:categories.models')}
              </span>
            </div>

            <h1 className="text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              {t(`common:categories.${category.key}`)}
            </h1>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                {t('common:productShowcase.title')}
              </h2>
              <p className="text-sm text-gray-600">
                {categoryProducts.length} {t('common:productShowcase.productsFound')}
              </p>
            </div>

            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              {t('common:nav.home')}
            </Link>
          </div>

          {categoryProducts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-product-shop>
              {categoryProducts.map((product) => {
                const seriesKey = getSeriesKey(product.series);
                return (
                  <Link 
                    key={product.id}
                    to={`/product/${product.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="aspect-square rounded-2xl overflow-hidden bg-gray-50 mb-4 flex items-center justify-center p-8">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="bg-gray-50 rounded-xl p-6">
                      <h3 className="text-base font-semibold text-gray-900 mb-1">{product.name}</h3>
                      <p className="text-sm text-amber-600 mb-2">{product.series}</p>
                      <p className="text-xs text-gray-600 mb-3 line-clamp-2">
                        {t(`products:series.${seriesKey}.shortDescription`)}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                        <i className="ri-lock-line"></i>
                        <span>{t('common:productCTA.dealerOnly')}</span>
                      </div>
                      <span className="text-sm text-amber-600 hover:text-amber-700 underline inline-flex items-center gap-1 cursor-pointer">
                        {t('common:productCTA.viewDetails')}
                        <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16">
              <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-gray-600">{t('common:productShowcase.noProducts')}</p>
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <i className="ri-shield-check-line text-5xl text-amber-600" style={{ width: '48px', height: '48px' }}></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('common:usps.europeanBrand.title')}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                {t('common:usps.europeanBrand.description')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <i className="ri-truck-line text-5xl text-amber-600" style={{ width: '48px', height: '48px' }}></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('common:usps.fastDelivery.title')}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                {t('common:usps.fastDelivery.description')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <i className="ri-customer-service-2-line text-5xl text-amber-600" style={{ width: '48px', height: '48px' }}></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('common:usps.fullSupport.title')}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                {t('common:usps.fullSupport.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <ProductInterestCTA variant="full" />
        </div>
      </section>

      <Footer />
    </div>
  );
}
